import {
  computePalette,
  readableForeground,
  type PaletteScheme,
} from "@/lib/handyout-palette";

/**
 * Emits the computed palette as a scoped stylesheet rule.
 *
 * Two constraints force this shape and neither is obvious:
 *
 * 1. It overrides `--handyout-chart-1..5`, never `--chart-1..5`. The modules do
 *    not read `--chart-N` from the cascade — `imports-token-scope.ts` pins them
 *    inline to `var(--handyout-chart-N)`, and an inline declaration wins the
 *    name against everything. Setting `--chart-N` here would lose to that pin;
 *    setting `--handyout-chart-N` reaches every module through it.
 *
 * 2. It is a `<style>` rule and not an inline style, because an inline
 *    declaration holds exactly one value and a handyout needs a light and a
 *    dark fassung of the same token name. The same reasoning applies to
 *    `--primary`, which the reader routes otherwise pin inline: when a scheme
 *    is active that pin has to be dropped, since it would beat this rule.
 */

const SCHEMES: readonly string[] = ["analog", "monochrome", "grayscale"];

/**
 * Nothing unvalidated may reach the style text, so both interpolated parts are
 * tested rather than escaped: the palette is written only if every slot matches
 * a six-digit hex, and the scope only if it is alphanumeric. `computePalette`
 * normalises its input through a hex parser and falls back to `DEFAULT_PRIMARY`
 * for anything unparseable, so this is a second line and not the first one.
 */
const HEX = /^#[0-9a-fA-F]{6}$/;
const SCOPE = /^[A-Za-z0-9_-]+$/;

function slotDeclarations(slots: readonly string[]): string | null {
  if (slots.length !== 5) return null;
  let out = "";
  for (let index = 0; index < 5; index += 1) {
    const hex = slots[index];
    if (typeof hex !== "string" || !HEX.test(hex)) return null;
    out += `--handyout-chart-${index + 1}:${hex};`;
    out += `--handyout-chart-${index + 1}-fg:${readableForeground(hex)};`;
  }
  return `${out}--primary:${slots[0]};`;
}

export function HandyoutPaletteStyle({
  primary,
  scheme,
  scopeId,
}: {
  primary: string | null | undefined;
  scheme: PaletteScheme | null | undefined;
  scopeId: string | null | undefined;
}): React.JSX.Element | null {
  if (!scheme || !SCHEMES.includes(scheme)) return null;
  if (!primary || !scopeId || !SCOPE.test(scopeId)) return null;

  const palette = computePalette(primary, scheme);
  const light = slotDeclarations(palette.light);
  const dark = slotDeclarations(palette.dark);
  if (!light || !dark) return null;

  const scope = `[data-handyout-palette="${scopeId}"]`;
  /* `.dark` lives on `<html>` on the reader routes, so the descendant form is
     the one that fires there; the self form covers a host that themes the
     scoped element itself, such as a preview pane. */
  const follow = `.dark [data-handyout-theme="follow"] ${scope},.dark ${scope}[data-handyout-theme="follow"]`;
  const css = `${scope}{${light}}.dark ${scope},.dark${scope}{${dark}}.light ${scope},.light${scope},[data-dashboard-theme] ${scope}{${light}}${follow}{${dark}}`;

  return <style>{css}</style>;
}
